import { useMemo, useState } from 'react';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { UpIcon } from 'foundations-assets/icons/components/UpIcon';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { SkeletonWrapper } from 'academy-common-lib/components/Skeletons/subcomponents/Skeleton';
import { OverviewContent } from '../overview/components/OverviewContent';
import SectionHeading from '../../components/SectionHeading';
import InstructorList from '../../components/InstructorList';
import UsageTracker from '../../utils/UsageTracker';
import { ModalTileSection } from '../../components/TrackLessonOverviewModal/subcomponents/ModalTileSection';
import { useEventInstructors, useFullLiveEvent } from 'academy-common-lib/features/liveEvents/liveEventQueries';
import { MYLEARNING_URL_FRAGMENT } from 'academy-common-lib/routes/academyRoutePaths';
import { LiveEventRegistrationAlert } from 'academy-common-lib/features/liveEvents/LiveEventRegistrationAlert';
import { getInProgressSession, getUpcomingSessions } from 'academy-common-lib/features/liveEvents/sessionTimeUtils';
import { SessionCard } from 'academy-common-lib/features/liveEvents/components/SessionCard';
import { RegistrationConfirmationModal } from 'academy-common-lib/features/liveEvents/RegistrationConfirmationModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const INITIAL_SESSIONS_SHOWN = 2;
const VIEW_REGISTERED_EVENTS_TO = `${MYLEARNING_URL_FRAGMENT}?contentType=LIVE_EVENT`;

// `InstructorList` types `descriptions` as the lesson shape ({ english, ... }) but
// reads `descriptions[language.toLowerCase()]` at runtime, so our CHIRP short-code
// shape ({ en, fr, ... }) works as-is. The cast is scoped to that one field.
// Metadata + socialMediaLinks are omitted because they aren't read by the renderer
// and their CHIRP enum types don't line up with the lesson `Professor` shape.
const toInstructorListProfessor = p => {
  var _p$contentId;
  return {
    contentId: (_p$contentId = p.contentId) !== null && _p$contentId !== void 0 ? _p$contentId : p.id,
    id: p.id,
    name: p.name,
    image: p.image,
    jobTitle: p.jobTitle,
    organisation: p.organisation,
    organisationUrl: p.organisationUrl,
    updatedAt: p.updatedAt,
    descriptions: p.descriptions
  };
};
function EventInstructorList({
  handleOrganisationUrlClick,
  instructorIds,
  language
}) {
  const {
    professors,
    loading,
    error
  } = useEventInstructors(instructorIds);
  if (loading) {
    return /*#__PURE__*/_jsx(SkeletonWrapper, {
      borderRadius: "300",
      height: 312,
      marginBlockStart: "800",
      width: "100%"
    });
  }
  if (error) {
    Raven.captureException(error);
    return null;
  }
  if (professors.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(InstructorList, {
    handleOrganisationUrlClick: handleOrganisationUrlClick,
    language: language.toLowerCase(),
    professors: professors.map(toInstructorListProfessor)
  });
}
const LiveEventOverviewModalContainer = ({
  liveEventId,
  language
}) => {
  var _sessions$find, _upcomingSessions$fin, _assignmentBySessionI, _learningEvent$instru, _registeredAssignment;
  const {
    isMobile
  } = useMediaQuery();
  const {
    assignments,
    error,
    liveEvent: learningEvent,
    loading,
    sessions
  } = useFullLiveEvent(liveEventId);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [registeredAssignment, setRegisteredAssignment] = useState(null);
  const registeredSession = (_sessions$find = sessions.find(s => s.id === (registeredAssignment === null || registeredAssignment === void 0 ? void 0 : registeredAssignment.sessionId))) !== null && _sessions$find !== void 0 ? _sessions$find : null;
  const handleRegistered = data => {
    setRegisteredAssignment({
      sessionId: data.registerForSession.sessionId,
      joinLink: data.registerForSession.joinLink || null
    });
  };

  /* This section will change depending on the data from the API */
  const registeredSessionIds = useMemo(() => new Set(assignments.filter(a => a.status === 'registered').map(a => a.sessionId)), [assignments]);
  const assignmentBySessionId = useMemo(() => new Map(assignments.filter(a => a.status === 'registered').map(a => [a.sessionId, a])), [assignments]);
  const liveSession = useMemo(() => getInProgressSession(sessions), [sessions]);
  const upcomingSessions = useMemo(() => getUpcomingSessions(sessions), [sessions]);
  const nextRegisteredUpcomingSession = (_upcomingSessions$fin = upcomingSessions.find(s => registeredSessionIds.has(s.id))) !== null && _upcomingSessions$fin !== void 0 ? _upcomingSessions$fin : null;

  // TODO: re-enable once CHIRP LearningEventSession exposes a recording link.
  // const pastRecording = useMemo(() => {
  //   const session = getPastSessions(sessions).find(
  //     (s) => s.recordingLink && assignmentBySessionId.has(s.id)
  //   );
  //   if (!session?.recordingLink) {
  //     return null;
  //   }
  //   const attended = assignmentBySessionId.get(session.id)?.attended ?? false;
  //   return { attended, recordingUrl: session.recordingLink };
  // }, [assignmentBySessionId, sessions]);
  const pastRecording = null;
  const isLiveSessionRegistered = liveSession ? registeredSessionIds.has(liveSession.id) : false;
  const liveSessionJoinUrl = liveSession && isLiveSessionRegistered ? ((_assignmentBySessionI = assignmentBySessionId.get(liveSession.id)) === null || _assignmentBySessionI === void 0 ? void 0 : _assignmentBySessionI.joinLink) || null : null;
  const visibleUpcomingSessions = useMemo(() => {
    if (showAllSessions) {
      return upcomingSessions;
    }
    const initial = upcomingSessions.slice(0, INITIAL_SESSIONS_SHOWN);
    const initialIds = new Set(initial.map(s => s.id));
    const hiddenRegistered = upcomingSessions.filter(s => registeredSessionIds.has(s.id) && !initialIds.has(s.id));
    return [...initial, ...hiddenRegistered];
  }, [showAllSessions, upcomingSessions, registeredSessionIds]);
  const getOrganisationUrlClickHandler = (professorId, organisation, organisationUrl) => () => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'professor-organisation-click',
      contentType: 'LIVE_EVENT',
      appearsIn: 'live-event-overview',
      organisation,
      organisationUrl,
      professorId,
      contentId: liveEventId
    });
  };
  return /*#__PURE__*/_jsxs(OverviewContent, {
    assetLanguage: language,
    backgroundColor: "fill-surface-recessed",
    error: error,
    fallbackThumbnailUrl: learningEvent === null || learningEvent === void 0 ? void 0 : learningEvent.previewThumbnailRectangleUrl,
    loading: loading,
    children: [/*#__PURE__*/_jsxs(View, {
      backgroundColor: "fill-surface-recessed",
      paddingInline: isMobile ? '300' : '800',
      children: [/*#__PURE__*/_jsx(LiveEventRegistrationAlert, {
        hasRegisteredUpcoming: !!nextRegisteredUpcomingSession,
        isLiveSessionRegistered: isLiveSessionRegistered,
        joinUrl: liveSessionJoinUrl,
        liveEventId: liveEventId,
        liveSession: liveSession,
        onRegistered: handleRegistered,
        pastRecording: pastRecording,
        viewRegisteredEventsTo: VIEW_REGISTERED_EVENTS_TO
      }), /*#__PURE__*/_jsx(ModalTileSection, {
        padding: "0",
        paddingBlockStart: "500",
        children: /*#__PURE__*/_jsx(Text, {
          as: isMobile ? 'h4' : 'h2',
          marginBlockEnd: "400",
          typography: "heading-600",
          children: learningEvent === null || learningEvent === void 0 ? void 0 : learningEvent.name
        })
      }), /*#__PURE__*/_jsx(SectionHeading, {
        paddingBlockEnd: "400",
        paddingBlockStart: "800",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.liveEventOverviewModal.eventDetails"
        })
      }), /*#__PURE__*/_jsx(CardWrapper, {
        marginBlockEnd: "0",
        children: /*#__PURE__*/_jsx(ModalTileSection, {
          paddingBlockEnd: "600",
          children: learningEvent === null || learningEvent === void 0 ? void 0 : learningEvent.description
        })
      }), /*#__PURE__*/_jsx(SectionHeading, {
        paddingBlockEnd: "400",
        paddingBlockStart: "800",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.liveEventOverviewModal.upcomingSessions"
        })
      }), upcomingSessions.length > 0 ? /*#__PURE__*/_jsxs(View, {
        children: [/*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          children: visibleUpcomingSessions.map(session => /*#__PURE__*/_jsx(SessionCard, {
            isRegistered: registeredSessionIds.has(session.id),
            liveEventId: liveEventId,
            onRegistered: handleRegistered,
            session: session
          }, session.id))
        }), /*#__PURE__*/_jsx(Flex, {
          justifyContent: isMobile ? 'center' : 'start',
          children: (showAllSessions || visibleUpcomingSessions.length < upcomingSessions.length) && /*#__PURE__*/_jsx(Button, {
            onClick: () => setShowAllSessions(prev => !prev),
            responsive: false,
            size: "small",
            use: "transparent",
            children: showAllSessions ? /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.liveEventOverviewModal.showLess"
              }), ' ', /*#__PURE__*/_jsx(UpIcon, {})]
            }) : /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.liveEventOverviewModal.showMore"
              }), ' ', /*#__PURE__*/_jsx(DownIcon, {})]
            })
          })
        })]
      }) : /*#__PURE__*/_jsx(CardWrapper, {
        marginBlockEnd: "0",
        children: /*#__PURE__*/_jsx(ModalTileSection, {
          paddingBlockEnd: "600",
          children: /*#__PURE__*/_jsx(Text, {
            typography: "heading-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.liveEventOverviewModal.noSessionsScheduled"
            })
          })
        })
      }), !!(learningEvent !== null && learningEvent !== void 0 && (_learningEvent$instru = learningEvent.instructorIds) !== null && _learningEvent$instru !== void 0 && _learningEvent$instru.length) && /*#__PURE__*/_jsx(EventInstructorList, {
        handleOrganisationUrlClick: getOrganisationUrlClickHandler,
        instructorIds: learningEvent.instructorIds,
        language: language
      }), /*#__PURE__*/_jsx(View, {
        paddingBlockEnd: "800"
      })]
    }), registeredSession && learningEvent && /*#__PURE__*/_jsx(RegistrationConfirmationModal, {
      isLive: (liveSession === null || liveSession === void 0 ? void 0 : liveSession.id) === registeredSession.id,
      joinUrl: (_registeredAssignment = registeredAssignment === null || registeredAssignment === void 0 ? void 0 : registeredAssignment.joinLink) !== null && _registeredAssignment !== void 0 ? _registeredAssignment : null,
      liveEventDescription: learningEvent.description,
      liveEventTitle: learningEvent.name,
      onClose: () => setRegisteredAssignment(null),
      session: registeredSession
    })]
  });
};
export default LiveEventOverviewModalContainer;